import { RubricTypeEnum } from 'susy-common/models';
import { SupervisorRole } from '../definitions/enums/SupervisorRoleEnum';
import {
    ExtensionRequestFileOfProofType,
    ExtensionRequestState,
    InvitationStatus,
    ProjectEditableDatesEnum,
    ProjectRegistrationAdminApprovalOutcomeEnum,
    ProjectSecondSupervisorStatus,
    ProjectStatus,
    ProjectUserChangesStatusEnum,
} from '../utils/utils';

export type NewValueChangeType =
    | ProjectStatusChange
    | ProjectStatusChangeAuto
    | ProjectStatusChangeAfterDeadline
    | ProjectStatusChangeByAdmin
    | TitleChangeByAdmin
    | ExtensionRequestChange
    | SecondSupervisorChange
    | SecondSupervisorChangeByAdmin
    | SupervisorChangeByAdmin
    | TeamMemberChange
    | TeamMemberChangeByAdmin
    | OwnerChangeByAdmin
    | AdminApproveProject
    | AdminSecondAttempt
    | AdminSecondAttemptStudents
    | ProjectDecline
    | SecondSupervisorDecline
    | ProjectChangedByUser
    | ProjectDeadlinesChangeByAdmin
    | ProjectDeadlineChangedByTimelineChange
    | ProjectChangeDateByAdmin
    | ProjectChangeDateOrDeadlineByAdmin
    | ProjectGradingReportUpdated
    | ProjectResetGrading
    | ProjectExtensionRequestChangeByAdmin
    | AdminUploadNewProjectFile
    | AdminDeletedProjectFile
    | AdminSetProjectFile
    | GradingChanged
    | GradingAcceptDecline
    | ProjectRegistrationRequiresApproval
    | UnlockProjectGradingByAdmin
    | ResetProjectGradingByAdmin;

export interface ChangeProjectVersion<NewValueChangeType> {
    changeDate: Date;
    changedBy: string;
    changedByName: string;
    propertyChanged: PropertyChanged;
    newValueChange: NewValueChangeType;
}

export enum PropertyChanged {
    ProjectStatusChange = 'ProjectStatusChange',
    ProjectStatusChangeAuto = 'ProjectStatusChangeAuto',
    ProjectStatusChangeAfterDeadline = 'ProjectStatusChangeAfterDeadline',
    ProjectStatusChangeByAdmin = 'ProjectStatusChangeByAdmin',
    TitleChangeByAdmin = 'TitleChangeByAdmin',
    ExtensionRequestChange = 'ExtensionRequestChange',
    SecondSupervisorChange = 'SecondSupervisorChange',
    SecondSupervisorChangeByAdmin = 'SecondSupervisorChangeByAdmin',
    SupervisorChangeByAdmin = 'SupervisorChangeByAdmin',
    TeamMemberChange = 'TeamMemberChange',
    TeamMemberChangeByAdmin = 'TeamMemberChangeByAdmin',
    OwnerChangeByAdmin = 'OwnerChangeByAdmin',
    AdminApproveProject = 'AdminApproveProject',
    AdminSecondAttempt = 'AdminSecondAttempt',
    AdminSecondAttemptStudents = 'AdminSecondAttemptStudents',
    ProjectDecline = 'ProjectDecline',
    SecondSupervisorDecline = 'SecondSupervisorDecline',
    UserChangeProject = 'UserChangeProject',
    AdminChangeProject = 'AdminChangeProject',
    ProjectDeadlinesChangeByAdmin = 'ProjectDeadlinesChangeByAdmin',
    ProjectDeadlinesChangeByTimelineChange = 'ProjectDeadlinesChangeByTimelineChange',
    ProjectExtensionRequestChangeByAdmin = 'ProjectExtensionRequestChangeByAdmin',
    AdditionalFileUploadByAdmin = 'AdditionalFileUploadByAdmin',
    ProjectChangeDateByAdmin = 'ProjectChangeDateByAdmin',
    ProjectChangeDateOrDeadlineByAdmin = 'ProjectChangeDateOrDeadlineByAdmin',
    ProjectGradingReportUpdated = 'ProjectGradingReportUpdated',
    ProjectResetGrading = 'ProjectResetGrading',
    AdminUpdateProjectFiles = 'AdminUpdateProjectFiles',
    AdminUploadNewProjectFile = 'AdminUploadNewProjectFile',
    AdminSetProjectMainFile = 'AdminSetProjectMainFile',
    AdminSetProjectPresentationFile = 'AdminSetProjectPresentationFile',
    AdminUnsetProjectMainFile = 'AdminUnsetProjectMainFile',
    AdminUnsetProjectPresentationFile = 'AdminUnsetProjectPresentationFile',
    AdminDeletedProjectFile = 'AdminDeletedProjectFile',
    GradingChanged = 'GradingChanged',
    GradingAcceptedDeclined = 'GradingAcceptedDeclined',
    GradingAcceptRejectSharedAssessment = 'GradingAcceptRejectSharedAssessment',
    ProjectRegistrationRequiresApproval = 'ProjectRegistrationRequiresApproval',
    UnlockProjectGradingByAdmin = 'UnlockProjectGradingByAdmin',
    ResetProjectGradingByAdmin = 'ResetProjectGradingByAdmin',
    ProjectFileRemoved = 'ProjectFileRemoved',
    AdminExternalSupervisor = 'AdminExternalSupervisor',
    ProjectSetAsFailed = 'ProjectSetAsFailed',
    ProjectSetAsFailedAutomatically = 'ProjectSetAsFailedAutomatically',
    ProjectRevertFailedStatus = 'ProjectRevertFailedStatus',
    AdminAddAdditionalSupervisor = 'AdminAddAdditionalSupervisor',
}

export enum GradingChangedEnum {
    SubmittedNonScoringGrade = 'SubmittedNonScoringGrade',
    ReSubmittedNonScoringGrade = 'ReSubmittedNonScoringGrade',
    SubmittedScoringGrade = 'SubmittedScoringGrade',
    ReSubmittedScoringGrade = 'ReSubmittedScoringGrade',
    SubmittedPresentationGrade = 'SubmittedPresentationGrade',
    ReSubmittedPresentationGrade = 'ReSubmittedPresentationGrade',
    SubmittedScoringGradeAsShared = 'SubmittedScoringGradeAsShared',
    ReSubmittedScoringGradeAsShared = 'ReSubmittedScoringGradeAsShared',
    SubmittedPresentationGradeAsShared = 'SubmittedPresentationGradeAsShared',
    ReSubmittedPresentationGradeAsShared = 'ReSubmittedPresentationGradeAsShared',
}

export enum UserChangeTypeByAdmin {
    Add = 'Add',
    Delete = 'Delete',
}

export enum AdminApprovalOutcomeEnum {
    APPROVED = 'APPROVED',
    DECLINED = 'DECLINED',
    PENDING = 'PENDING',
}

export enum AdminSecondAttemptFlag {
    FLAGGED_AS_SECOND_ATTEMPT = 'FLAGGED_AS_SECOND_ATTEMPT',
    UNFLAGGED_AS_SECOND_ATTEMPT = 'UNFLAGGED_AS_SECOND_ATTEMPT',
}

export interface AdminApproveProject {
    approvalOutcome: AdminApprovalOutcomeEnum;
    reason: string;
}

export interface AdminSecondAttempt {
    flagged: AdminSecondAttemptFlag;
}

export interface AdminSecondAttemptStudents {
    secondAttemptStudentNames: Array<string>;
}

export enum GradingAcceptDeclineEnum {
    Accepted = 'Accepted',
    Declined = 'Declined',
}

export interface GradingAcceptDecline {
    gradingAcceptDecline: GradingAcceptDeclineEnum;
    supervisorRole: SupervisorRole;
}

export enum GradingAcceptRejectSharedAssessmentEnum {
    Accepted = 'Accepted',
    Rejected = 'Rejected',
}

export interface GradingAcceptRejectSharedAssessment {
    gradingAcceptReject: GradingAcceptRejectSharedAssessmentEnum;
    studentName: string | undefined;
    rubricType: RubricTypeEnum;
    supervisorWhosAssessmentGotAccepted: string;
}

export interface GradingChanged {
    gradingChange: GradingChangedEnum;
    supervisorRole: SupervisorRole;
    studentName?: string;
    note?: string;
}

export interface AdminAddAdditionalSupervisor {
    supervisorName?: string;
    note?: string;
}

export interface ProjectStatusChange {
    status: ProjectStatus;
}

export type ProjectStatusChangeAuto = ProjectStatusChange;

export interface ProjectStatusChangeAfterDeadline {
    status: ProjectStatus;
    deadline: Date;
}

export interface ProjectDecline extends ProjectStatusChange {
    declineReason: string;
}

export interface ProjectStatusChangeByAdmin {
    status: ProjectStatus;
    note?: string;
}

export interface ExtensionRequestChange {
    type: ExtensionRequestFileOfProofType;
    state: ExtensionRequestState;
    note?: string;
}

export interface SecondSupervisorChange {
    status: ProjectSecondSupervisorStatus;
    name: string;
}

export interface SecondSupervisorDecline extends SecondSupervisorChange {
    declineReason: string;
}

export interface SecondSupervisorChangeByAdmin {
    type: UserChangeTypeByAdmin;
    status: ProjectSecondSupervisorStatus;
    name: string;
}

export interface SupervisorChangeByAdmin {
    type: UserChangeTypeByAdmin;
    name: string;
}

export interface TeamMemberChange {
    invitationStatus: InvitationStatus;
    name: string;
}

export interface TeamMemberChangeByAdmin {
    type: UserChangeTypeByAdmin;
    invitationStatus: InvitationStatus;
    name: string;
}

export interface OwnerChangeByAdmin {
    type: UserChangeTypeByAdmin;
    name: string;
}

export interface ProjectChangedByUser {
    newTitle?: string;
    oldTitle?: string;
    newResearchAreaTitle?: string;
    oldResearchAreaTitle?: string;
    newResearchAreaOverview?: string;
    oldResearchAreaOverview?: string;
    newLiterature?: Array<string>;
    oldLiterature?: Array<string>;
    status?: ProjectUserChangesStatusEnum;
    declineDescription?: string;
    note?: string;
}
export interface ProjectDeadlines {
    submission: Date;
    evaluation: Date;
}
export interface ProjectDeadlinesChangeByAdmin {
    oldDeadlines?: ProjectDeadlines;
    newDeadlines: ProjectDeadlines;
    note?: string;
}

export interface ProjectDeadlineChangedByTimelineChange {
    oldDeadlines: ProjectDeadlines;
    newDeadlines: ProjectDeadlines;
    timelineName: string;
}

export interface ProjectExtensionRequestChangeByAdmin {
    newNumberOfDays: number;
    sentBy: string;
    sentDate: Date;
    type: ExtensionRequestFileOfProofType;
    note?: string;
}

export interface AdditionalFileUploadByAdmin {
    file: ExtensionRequestFileOfProofType;
    note?: string;
}

export interface AdminUpdateProjectFiles {
    addedFiles: { name: string; size: number }[];
    removedFiles: { name: string; size: number }[];
}

export interface ProjectChangeDateByAdmin {
    newDate: Date;
    note?: string;
    projectDateType: ProjectStatus;
}

export interface ProjectChangeDateOrDeadlineByAdmin {
    projectEditedDate: ProjectEditableDatesEnum;
    oldProjectDateValue: Date;
    newProjectDateValue: Date;
    note?: string;
}

export interface TitleChangeByAdmin {
    title: string;
}

export interface UnlockProjectGradingByAdmin {
    supervisors: string[];
}

export interface ResetProjectGradingByAdmin {
    supervisors: string[];
}

export interface AdminUploadNewProjectFile {
    name: string;
    size: number;
}

export interface AdminDeletedProjectFile {
    name: string;
    size: number;
}
export interface AdminSetProjectFile {
    name: string;
}

export interface ProjectRegistrationRequiresApproval {
    status: ProjectRegistrationAdminApprovalOutcomeEnum;
    note: string;
}

export interface ProjectGradingReportUpdated {
    note?: string;
}

export interface ProjectResetGrading {
    status: ProjectStatus;
    node?: string;
}

export interface ProjectFileRemovalByAdmin {
    name: string;
    note?: string;
}

export interface AdminAcceptExternalSupervisor {
    status: AdminAcceptExternalSupervisorStatus;
    name: string;
    reason?: string;
}

export enum AdminAcceptExternalSupervisorStatus {
    ACCEPTED = 'ACCEPTED',
    DECLINED = 'DECLINED',
}

export interface ProjectSetAsFailedByAdmin {
    status: ProjectStatus;
    reasonForFailedProject: string;
}

export interface ProjectRevertFailedStatusByAdmin {
    status: ProjectStatus;
}
